'use client';

import React from 'react';
import { Icon, IconType } from '../icons';
import { TAccordionProps, AccordionProfileEnum, cn } from 'types';

const profileStyles: Record<AccordionProfileEnum, { trigger: string; content: { true: string; false: string } }> = {
  [AccordionProfileEnum.DEFAULT]: {
    trigger: 'px-4 py-2',
    content: {
      true: 'px-4 pt-6 pb-2 grid-rows-[1fr]',
      false: 'px-4 grid-rows-[0fr]',
    },
  },
  [AccordionProfileEnum.MEDIUM]: {
    trigger: 'p-4',
    content: {
      true: 'px-4 pt-2 pb-4 grid-rows-[1fr]',
      false: 'px-4 grid-rows-[0fr]',
    },
  },
  [AccordionProfileEnum.HIGH]: {
    trigger: 'px-4 py-6',
    content: {
      true: 'px-4 pt-2 pb-4 grid-rows-[1fr]',
      false: 'px-4 grid-rows-[0fr]',
    },
  },
};

export const Accordion = ({
  children,
  isDisabled = false,
  defaultOpen = false,
  primaryLabel,
  // secondaryLabel,
  customLabel,
  distanceLabel,
  className,
  triggerClassName,
  contentClassName,
  toggleClassName,
  accordionProfile = AccordionProfileEnum.DEFAULT,
  hideDropdownIcon = false,
  onClick,
  isOpen,
  reservationInfoBar,
}: Partial<TAccordionProps>) => {
  const [open, setOpen] = React.useState<boolean>(defaultOpen);

  React.useEffect(() => {
    setOpen(defaultOpen);
  }, [defaultOpen]);

  const greyMode = className?.includes('tw-grey-mode');
  const borderY = className?.includes('tw-border-y');

  React.useEffect(() => {
    if (isOpen) {
      isOpen(open);
    }
  }, [open]);

  return (
    <div
      aria-expanded={open}
      className={cn(
        'tw-accordion h-fit overflow-hidden rounded-lg border aria-expanded:border-black aria-expanded:outline-black aria-expanded:outline border-neutralPlus',
        borderY && 'border-x-0 outline-none rounded-none aria-expanded:border-neutralPlus',
        greyMode && 'border-none outline-none',
        className
      )}
    >
      <button
        type="button"
        disabled={isDisabled}
        onClick={() => {
          if (!isDisabled) {
            typeof onClick === 'function' && onClick();
            setOpen(!open);
          }
        }}
        className={cn(
          'flex w-full items-center justify-between gap-6 text-left',
          'transition-colors duration-200',
          !open && 'enabled:hover:bg-neutralGrey',
          isDisabled ? 'cursor-not-allowed' : 'cursor-pointer',
          greyMode ? 'bg-lightGrey border-none rounded-lg' : 'bg-white',
          profileStyles[accordionProfile]['trigger'],
          triggerClassName
        )}
        aria-expanded={open}
      >
        <div className="flex flex-col flex-1">
          {primaryLabel && <h4 className={cn('text-lg/[150%] font-semibold')}>{primaryLabel}</h4>}
          {customLabel}
        </div>
        <div className="flex gap-6 empty:hidden">
          {distanceLabel && <div className="font-semibold">{distanceLabel}</div>}
          {!hideDropdownIcon && (
            <div
              className={cn(
                'tw-accordion-icon-wrapper',
                'bg-white rounded-full transition-transform duration-350 ease-in-out',
                open ? 'rotate-180' : 'rotate-0',
                toggleClassName
              )}
            >
              <Icon icon={IconType.ChevronDown} width={24} height={24} className="tw-accordion-icon" />
            </div>
          )}
        </div>
      </button>

      <div
        aria-expanded={open}
        className={cn(
          'bg-white grid transition-all duration-350 ease-in-out',
          profileStyles[accordionProfile]['content'][open ? 'true' : 'false'],
          contentClassName
        )}
      >
        {open && (
          <>
            {reservationInfoBar}
            <div className="w-full">{children}</div>
          </>
        )}
      </div>
    </div>
  );
};
